import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, Download, Filter, Package, TrendingUp, Users, Building2, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { productAPI, subCategorySalesReportAPI } from '../../services/api';
import { exportToCSV } from '../../utils/exportCSV';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const GRANULARITIES = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'custom', label: 'Custom' },
];

const fmtINR = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const fmtTon = (n) => `${(Number(n) || 0).toFixed(3)}`;
const today = () => new Date().toISOString().slice(0, 10);

// ── Pivot helpers ──────────────────────────────────────────────────────────
// Build a pivot from long-format rows:
//   rows: [{ row_key, col_key, value }]
//   Returns:
//   {
//     rowKeys: [sorted alphabetically],
//     colKeys: [sorted alphabetically],
//     grid: { row_key: { col_key: value } },
//     rowTotals: { row_key: total },
//     colTotals: { col_key: total },
//     grandTotal: number
//   }
const buildPivot = (rows, rowField, colField, valueField) => {
  const grid = {};
  const colsSet = new Set();
  const rowsSet = new Set();
  let grandTotal = 0;
  const rowTotals = {};
  const colTotals = {};
  rows.forEach(r => {
    const rk = r[rowField] || '—';
    const ck = r[colField] || '—';
    const v = Number(r[valueField] || 0);
    rowsSet.add(rk); colsSet.add(ck);
    grid[rk] = grid[rk] || {};
    grid[rk][ck] = (grid[rk][ck] || 0) + v;
    rowTotals[rk] = (rowTotals[rk] || 0) + v;
    colTotals[ck] = (colTotals[ck] || 0) + v;
    grandTotal += v;
  });
  const rowKeys = [...rowsSet].sort((a, b) => a.localeCompare(b));
  const colKeys = [...colsSet].sort((a, b) => a.localeCompare(b));
  return { rowKeys, colKeys, grid, rowTotals, colTotals, grandTotal };
};

const PivotTable = ({ pivot, rowLabel, formatCell, formatTotal, testId }) => {
  const { rowKeys, colKeys, grid, rowTotals, colTotals, grandTotal } = pivot;
  if (rowKeys.length === 0) {
    return <div className="text-center py-10 text-sm text-gray-400">No data</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse" data-testid={testId}>
        <thead>
          <tr>
            <th className="text-left px-3 py-2 text-[10px] font-semibold text-white uppercase border border-white/10" style={{ backgroundColor: '#24549c' }}>{rowLabel}</th>
            {colKeys.map(c => (
              <th key={c} className="text-center px-3 py-2 text-[10px] font-semibold text-white uppercase border border-white/10 whitespace-nowrap" style={{ backgroundColor: '#24549c' }}>{c}</th>
            ))}
            <th className="text-right px-3 py-2 text-[10px] font-semibold text-white uppercase border border-white/10" style={{ backgroundColor: '#1a3d73' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rowKeys.map(r => (
            <tr key={r} className="hover:bg-blue-50/30">
              <td className="px-3 py-2 font-medium text-gray-900 border border-gray-200 bg-gray-50">{r}</td>
              {colKeys.map(c => {
                const v = grid[r]?.[c];
                return (
                  <td key={c} className="px-3 py-2 text-center text-gray-700 border border-gray-200">
                    {v == null || v === 0 ? '' : (formatCell ? formatCell(v) : v)}
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-200 bg-amber-50">
                {formatTotal ? formatTotal(rowTotals[r]) : rowTotals[r]}
              </td>
            </tr>
          ))}
          <tr className="font-bold text-white" style={{ backgroundColor: '#24549c' }}>
            <td className="px-3 py-2 border border-white/10 uppercase text-[10px]">Total</td>
            {colKeys.map(c => (
              <td key={c} className="px-3 py-2 text-center border border-white/10">
                {colTotals[c] == null || colTotals[c] === 0 ? '' : (formatTotal ? formatTotal(colTotals[c]) : colTotals[c])}
              </td>
            ))}
            <td className="px-3 py-2 text-right border border-white/10" style={{ backgroundColor: '#1a3d73' }}>
              {formatTotal ? formatTotal(grandTotal) : grandTotal}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ── Build an XLSX sheet array (array of arrays) from a pivot ──────────────
const pivotToAoA = (pivot, rowLabel, roundFn) => {
  const { rowKeys, colKeys, grid, rowTotals, colTotals, grandTotal } = pivot;
  const header = [rowLabel, ...colKeys, 'Total'];
  const body = rowKeys.map(r => [
    r,
    ...colKeys.map(c => {
      const v = grid[r]?.[c];
      return v == null || v === 0 ? '' : roundFn(v);
    }),
    roundFn(rowTotals[r] || 0),
  ]);
  const totalRow = [
    'Total',
    ...colKeys.map(c => (colTotals[c] == null || colTotals[c] === 0 ? '' : roundFn(colTotals[c]))),
    roundFn(grandTotal),
  ];
  return [header, ...body, totalRow];
};

// ──────────────────────────────────────────────────────────────────────────

const SubCategorySalesReport = () => {
  const { hasPermission } = usePermissions();
  const canAccess = hasPermission('reports.sub_category_sales');

  const [subCats, setSubCats] = useState([]);
  const [selectedSubCats, setSelectedSubCats] = useState([]);
  const [granularity, setGranularity] = useState('monthly');
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [showQuantity, setShowQuantity] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('value_sp_product');

  useEffect(() => {
    if (!canAccess) return;
    (async () => {
      try {
        const rows = await productAPI.listSubCategories();
        setSubCats(rows || []);
      } catch (err) { console.error(err); }
    })();
  }, [canAccess]);

  const toggleSubCat = (name) => {
    setSelectedSubCats(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]);
  };

  const fetchReport = async () => {
    if (selectedSubCats.length === 0) { toast.error('Select at least one sub-category'); return; }
    if (granularity === 'custom' && (!fromDate || !toDate)) { toast.error('Provide From & To dates'); return; }
    try {
      setLoading(true);
      const params = {
        sub_categories: selectedSubCats.join(','),
        granularity,
        show_quantity: showQuantity,
      };
      if (granularity === 'custom') { params.from_date = fromDate; params.to_date = toDate; }
      else { params.from_date = fromDate; }
      const res = await subCategorySalesReportAPI.getBreakdown(params);
      setData(res);
      if (res.summary.tonnes_applicable) setView('qty_sp_product');
      else setView('value_sp_product');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally { setLoading(false); }
  };

  // ── Pivots (memoised) ──
  const qtySpProductPivot = useMemo(() => buildPivot(data?.qty_salesperson_product || [], 'salesperson', 'product', 'qty_tonnes'), [data]);
  const valSpProductPivot = useMemo(() => buildPivot(data?.value_salesperson_product || [], 'salesperson', 'product', 'value'), [data]);
  const valSpBranchPivot = useMemo(() => buildPivot(data?.value_salesperson_branch || [], 'salesperson', 'branch', 'value'), [data]);
  const qtySpBranchPivot = useMemo(() => buildPivot(data?.qty_salesperson_branch || [], 'salesperson', 'branch', 'qty_tonnes'), [data]);

  const rnd3 = (v) => Math.round(v * 1000) / 1000;
  const rnd2 = (v) => Math.round(v * 100) / 100;

  // ── Exports ──
  const exportExcelAllSheets = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summary = data.summary;
    const filters = data.filters;
    const period = data.period;
    const summaryAoA = [
      ['Sub-category Sales Report'],
      [],
      ['Period', period.granularity],
      ['From', String(period.from).slice(0, 10)],
      ['To', String(period.to).slice(0, 10)],
      ['Sub-categories', (filters.sub_categories || []).join(', ')],
      ['Branch filter', filters.branch || 'All'],
      ['Salesperson filter', filters.salesman || 'All'],
      [],
      ['OCs Dispatched', summary.oc_count],
      ['Unique Customers', summary.unique_customers],
      ['Total Qty (Tonnes)', summary.tonnes_applicable ? rnd3(summary.total_qty_tonnes) : 'N/A'],
      ['Total Value (INR)', rnd2(summary.total_value)],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryAoA), 'Summary');

    // Pivots
    if (summary.tonnes_applicable) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(pivotToAoA(qtySpProductPivot, 'Sales Person', rnd3)), 'SP x Product (Tonnes)');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(pivotToAoA(qtySpBranchPivot, 'Sales Person', rnd3)), 'SP x Branch (Tonnes)');
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(pivotToAoA(valSpProductPivot, 'Sales Person', rnd2)), 'SP x Product (Value)');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(pivotToAoA(valSpBranchPivot, 'Sales Person', rnd2)), 'SP x Branch (Value)');

    // Raw data
    const raw = data.raw_data || [];
    const rawHeader = ['OC Number', 'OC Date', 'Dispatch Date', 'Customer', 'Salesperson', 'Branch',
                       'Product', 'Sub-category', 'Qty', 'Unit', 'Qty (Tonnes)', 'Rate', 'Amount', 'Invoice Number'];
    const rawBody = raw.map(r => [
      r.oc_number, r.oc_date, r.dispatch_date, r.customer, r.salesperson, r.branch,
      r.product, r.sub_category, r.qty, r.unit, r.qty_tonnes ?? '', r.rate, r.amount, r.invoice_number,
    ]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([rawHeader, ...rawBody]), 'Raw Data');

    const ts = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `sub-category-sales-${ts}.xlsx`);
    toast.success('Excel report downloaded');
  };

  const exportCurrentViewCSV = () => {
    if (!data) return;
    if (view === 'qty_sp_product') {
      exportToCSV(data.qty_salesperson_product, [
        { label: 'Salesperson', accessor: 'salesperson' },
        { label: 'Product', accessor: 'product' },
        { label: 'Qty (Tonnes)', accessor: 'qty_tonnes' },
      ], 'sp_product_tonnes');
    } else if (view === 'value_sp_product') {
      exportToCSV(data.value_salesperson_product, [
        { label: 'Salesperson', accessor: 'salesperson' },
        { label: 'Product', accessor: 'product' },
        { label: 'Sub-category', accessor: 'sub_category' },
        { label: 'Value (INR)', accessor: 'value' },
      ], 'sp_product_value');
    } else if (view === 'qty_sp_branch') {
      exportToCSV(data.qty_salesperson_branch, [
        { label: 'Salesperson', accessor: 'salesperson' },
        { label: 'Branch', accessor: 'branch' },
        { label: 'Qty (Tonnes)', accessor: 'qty_tonnes' },
      ], 'sp_branch_tonnes');
    } else if (view === 'value_sp_branch') {
      exportToCSV(data.value_salesperson_branch, [
        { label: 'Salesperson', accessor: 'salesperson' },
        { label: 'Branch', accessor: 'branch' },
        { label: 'Value (INR)', accessor: 'value' },
      ], 'sp_branch_value');
    } else if (view === 'qty_item_wise') {
      exportToCSV(data.qty_item_wise, [
        { label: 'Product', accessor: 'product' },
        { label: 'Sub-category', accessor: 'sub_category' },
        { label: 'Unit', accessor: 'unit' },
        { label: 'Qty', accessor: 'qty' },
      ], 'item_wise_qty');
    }
  };

  if (!canAccess) {
    return (
      <div className="p-8 text-center" data-testid="subcat-report-no-access">
        <p className="text-sm text-gray-500">
          You do not have permission to view this report. Ask an admin to grant
          <strong> Reports → View Sub-Category Sales Report</strong> under
          Settings → Role Permissions.
        </p>
      </div>
    );
  }

  const tonnesApplicable = data?.summary?.tonnes_applicable;

  const renderViewTabs = () => {
    const tabs = [];
    if (tonnesApplicable) {
      tabs.push({ id: 'qty_sp_product', label: 'SP × Product (Tonnes)', icon: Package });
      tabs.push({ id: 'qty_sp_branch', label: 'SP × Branch (Tonnes)', icon: Package });
    }
    tabs.push({ id: 'value_sp_product', label: 'SP × Product (Value)', icon: TrendingUp });
    tabs.push({ id: 'value_sp_branch', label: 'SP × Branch (Value)', icon: Building2 });
    if (!tonnesApplicable && showQuantity) tabs.push({ id: 'qty_item_wise', label: 'Item-wise Quantity', icon: Package });
    return (
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit mb-4 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} data-testid={`subcat-view-${t.id}`} onClick={() => setView(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold rounded-lg transition-all whitespace-nowrap ${
              view === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>
    );
  };

  const renderTable = () => {
    if (!data) return null;
    if (view === 'qty_sp_product') {
      return <PivotTable pivot={qtySpProductPivot} rowLabel="Sales Person Name" formatCell={fmtTon} formatTotal={fmtTon} testId="qty-sp-product-pivot" />;
    }
    if (view === 'qty_sp_branch') {
      return <PivotTable pivot={qtySpBranchPivot} rowLabel="Sales Person Name" formatCell={fmtTon} formatTotal={fmtTon} testId="qty-sp-branch-pivot" />;
    }
    if (view === 'value_sp_product') {
      return <PivotTable pivot={valSpProductPivot} rowLabel="Sales Person Name" formatCell={fmtINR} formatTotal={fmtINR} testId="value-sp-product-pivot" />;
    }
    if (view === 'value_sp_branch') {
      return <PivotTable pivot={valSpBranchPivot} rowLabel="Sales Person Name" formatCell={fmtINR} formatTotal={fmtINR} testId="value-sp-branch-pivot" />;
    }
    if (view === 'qty_item_wise') {
      const rows = data.qty_item_wise || [];
      return (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="qty-item-wise-table">
            <thead>
              <tr className="text-white" style={{ backgroundColor: '#24549c' }}>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase border border-white/10">Product</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase border border-white/10">Sub-category</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase border border-white/10">Unit</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase border border-white/10">Qty</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-gray-400">No data</td></tr>}
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-blue-50/30">
                  <td className="px-3 py-2 font-medium text-gray-900 border border-gray-200">{r.product}</td>
                  <td className="px-3 py-2 border border-gray-200">
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded-full bg-purple-50 text-purple-700">{r.sub_category}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-600 border border-gray-200">{r.unit}</td>
                  <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-200">{Number(r.qty).toLocaleString('en-IN', { maximumFractionDigits: 3 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  return (
    <div data-testid="sub-category-sales-report" className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <div>
          <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-700 mb-2">
            <Filter className="w-3 h-3" /> Sub-category (multi-select)
          </label>
          <div className="flex flex-wrap gap-1.5">
            {subCats.length === 0 && <span className="text-[11px] text-gray-400">No sub-categories defined yet. Assign some in Product Master.</span>}
            {subCats.map(s => {
              const active = selectedSubCats.includes(s.sub_category);
              return (
                <button key={s.sub_category} data-testid={`subcat-chip-${s.sub_category}`} onClick={() => toggleSubCat(s.sub_category)}
                  className={`px-3 py-1 text-[11px] rounded-full border transition-all ${active ? 'text-white border-transparent shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                  style={active ? { backgroundColor: '#24549c' } : {}}>
                  {s.sub_category} <span className={active ? 'opacity-80' : 'text-gray-400'}>({s.count})</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Period</label>
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              {GRANULARITIES.map(g => (
                <button key={g.id} data-testid={`granularity-${g.id}`} onClick={() => setGranularity(g.id)}
                  className={`px-3 py-1.5 text-[11px] font-semibold rounded-md transition-all ${granularity === g.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {granularity === 'custom' ? (
            <>
              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">From</label>
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">To</label>
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">Anchor Date</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              <p className="text-[9px] text-gray-400 mt-0.5">
                {granularity === 'daily' && 'Report for this day only'}
                {granularity === 'weekly' && 'Week containing this date (Mon–Sun)'}
                {granularity === 'monthly' && 'Calendar month containing this date'}
              </p>
            </div>
          )}

          <label className="flex items-center gap-1.5 text-[11px] text-gray-700 cursor-pointer">
            <input type="checkbox" data-testid="show-quantity-toggle" checked={showQuantity} onChange={e => setShowQuantity(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300" />
            Show item-wise quantity (non-Tonnes sub-categories)
          </label>

          <button data-testid="run-report-btn" onClick={fetchReport} disabled={loading || selectedSubCats.length === 0}
            className="ml-auto flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-40 hover:shadow-lg" style={{ backgroundColor: '#24549c' }}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Filter className="w-3.5 h-3.5" />} Run Report
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#24549c' }} />
          <span className="ml-2 text-sm text-gray-500">Crunching dispatched OCs...</span>
        </div>
      )}

      {!loading && data && (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 text-[10px] font-semibold text-gray-400 uppercase"><Users className="w-3 h-3" /> OCs Dispatched</div>
              <p className="text-xl font-bold text-gray-900 mt-1" data-testid="summary-oc-count">{data.summary.oc_count}</p>
              <p className="text-[10px] text-gray-400">{data.summary.unique_customers} unique customers</p>
            </div>
            {data.summary.tonnes_applicable && (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-2 text-[10px] font-semibold text-gray-400 uppercase"><Package className="w-3 h-3" /> Total Qty (Tonnes)</div>
                <p className="text-xl font-bold text-gray-900 mt-1" data-testid="summary-total-tonnes">{fmtTon(data.summary.total_qty_tonnes)} T</p>
                <p className="text-[10px] text-gray-400">{data.summary.tonnes_sub_categories.join(' + ')}</p>
              </div>
            )}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 text-[10px] font-semibold text-gray-400 uppercase"><TrendingUp className="w-3 h-3" /> Total Value</div>
              <p className="text-xl font-bold text-gray-900 mt-1" data-testid="summary-total-value">{fmtINR(data.summary.total_value)}</p>
              <p className="text-[10px] text-gray-400">Across selected sub-categories</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 text-[10px] font-semibold text-gray-400 uppercase"><Filter className="w-3 h-3" /> Period</div>
              <p className="text-sm font-bold text-gray-900 mt-1 capitalize" data-testid="summary-period">{data.period.granularity}</p>
              <p className="text-[10px] text-gray-400">{String(data.period.from).slice(0,10)} → {String(data.period.to).slice(0,10)}</p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap">
            {renderViewTabs()}
            <div className="flex items-center gap-2">
              <button data-testid="export-current-view-csv-btn" onClick={exportCurrentViewCSV}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
                <Download className="w-3 h-3" /> Current view CSV
              </button>
              <button data-testid="export-excel-btn" onClick={exportExcelAllSheets}
                className="flex items-center gap-1.5 px-4 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg" style={{ backgroundColor: '#0F766E' }}>
                <FileSpreadsheet className="w-3 h-3" /> Export Excel (all sheets + Raw Data)
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-2">
            {renderTable()}
          </div>
        </>
      )}

      {!loading && !data && (
        <div className="bg-white rounded-xl border border-gray-100 p-10 text-center text-sm text-gray-400" data-testid="subcat-report-empty">
          Select one or more sub-categories and a period, then click <span className="font-semibold text-gray-600">Run Report</span>.
        </div>
      )}
    </div>
  );
};

export default SubCategorySalesReport;
